/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, typography2 } from '@coursera/cds-core';

import { numberOfProductsWithPlusSign } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { icons } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';

import _t from 'i18n!nls/coursera-plus-common';

export const featureCardStyles = {
  featureCardsWrapper: css`
    margin: 0 0 20px;

    > div:first-child > div {
      padding-top: 24px;
      border-top: 1px solid var(--cds-color-grey-50);
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
    }

    > div:last-child > div {
      border-bottom: 1px solid var(--cds-color-grey-50);
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;
    }
  `,
  featureCardHeaderCell: css`
    text-align: center;
  `,
  featureCardCell: css`
    position: relative;
    width: 310px;
    padding: 0 24px 24px;
    box-sizing: border-box;
    border-left: 1px solid var(--cds-color-grey-50);
    border-right: 1px solid var(--cds-color-grey-50);
  `,
  primaryFeatureCardCell: css`
    border-color: var(--cds-color-blue-950) !important;
  `,
  taggedFeatureCardCell: css`
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  `,
  featureCardRow: css`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 24px;
  `,
  featureCardHeaderContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  `,
  featureCardLogoHeader: css`
    display: flex;
    gap: 4px;
    align-items: center;
  `,
  featureTag: css`
    position: absolute;
    width: calc(100% + 2px);
    top: -23px;
    left: -1px;
    display: flex;
    justify-content: center;
    height: 26px;
    border-radius: 0;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;

    span {
      /* stylelint-disable-next-line cds-stylelint/cds-typography-utility */
      font: var(--cds-typography-subtitle-medium);
    }
  `,
};

const valuePropsStyles = {
  valueProps: css`
    padding: 0;
    margin: 0;
    list-style-type: none;

    li {
      strong {
        ${typography2.subtitleMedium};
      }
    }
  `,
  valuePropListItem: css`
    display: flex;
    align-items: center;
    padding: 0 0 var(--cds-spacing-100) 0;

    &:last-child {
      padding-bottom: 0;
    }
  `,
  barIcon: css`
    margin-right: 6px;
  `,
};

const allPlansValuePropsFormatter = ({ message, value }: { message: string; value: string | React.JSX.Element }) => (
  <FormattedMessage message={message} value={value} />
);

const getCopy = ({ showPartnerProp, partnerCount = 1 }: { showPartnerProp?: boolean; partnerCount?: number }) => ({
  singleProgramValueProps: [
    allPlansValuePropsFormatter({ message: _t('{value} learning program'), value: <strong>1</strong> }),
    allPlansValuePropsFormatter({ message: _t('{value} certificate'), value: <strong>1</strong> }),
    ...(showPartnerProp
      ? [
          <FormattedMessage
            message={_t('{value} teaching {partnerCount, plural, =1 {institution} other {institutions}}')}
            partnerCount={partnerCount}
            value={<strong>{partnerCount}</strong>}
          />,
        ]
      : []),
  ],
  courseraPlusValueProps: [
    allPlansValuePropsFormatter({
      message: _t('{value} learning programs'),
      value: <strong>{numberOfProductsWithPlusSign}</strong>,
    }),
    allPlansValuePropsFormatter({ message: _t('{value} certificates'), value: <strong>{_t('Unlimited')}</strong> }),
    ...(showPartnerProp
      ? [allPlansValuePropsFormatter({ message: _t('{value} teaching institutions'), value: <strong>275+</strong> })]
      : []),
  ],
  courseraCoachAccess: allPlansValuePropsFormatter({
    message: _t('{value} access'),
    value: <strong>{_t('+ Coursera Coach')}</strong>,
  }),
});

export const getValueProps = ({
  isCourseraPlus,
  showCoachAccess,
  showPartnerProp,
  partnerCount,
}: {
  isCourseraPlus: boolean;
  showCoachAccess?: boolean;
  showPartnerProp?: boolean;
  partnerCount?: number;
}) => {
  const copy = getCopy({ showPartnerProp, partnerCount });
  const styles = valuePropsStyles;
  const valueProps = isCourseraPlus ? copy.courseraPlusValueProps : copy.singleProgramValueProps;
  return (
    <ul css={styles.valueProps}>
      {valueProps.map((valueProp) => (
        <li key={`value-prop-${valueProp}`} css={styles.valuePropListItem}>
          <Imgix
            src={isCourseraPlus ? icons.FULL_BAR : icons.SINGLE_BAR}
            width={20}
            css={styles.barIcon}
            alt={Imgix.DECORATIVE}
          />
          <Typography2 component="div">
            <Typography2 component="div" variant="bodyPrimary">
              {valueProp}
            </Typography2>
          </Typography2>
        </li>
      ))}
      {isCourseraPlus && showCoachAccess && (
        <li key="value-prop-coach-access" css={styles.valuePropListItem}>
          <Typography2 component="div">
            <Typography2 component="div" variant="actionPrimary">
              {copy.courseraCoachAccess}
            </Typography2>
          </Typography2>
        </li>
      )}
    </ul>
  );
};
